import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "./PortfolioPanel.css";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@mui/material";
import { useEffect } from "react";
import { techProfileGetUserPreviousDataAction } from "../../../actions/MyTechProfile/myTechProfileActions";
import MySkeletonLoader from "../reusables/MySkeletonLoader";

const testimonials = [
  {
    name: "Aman Verma",
    role: "Frontend Developer",
    company: "Tech Solutions Inc",
    profileImage:
      "https://ui-avatars.com/api/?name=Aman+Verma&background=0D8ABC&color=fff",
    text: "After creating my AceTechies portfolio, I started getting recruiter calls within 2 weeks. The professional presentation made all the difference!",
    timestamp: "2w",
    likes: 47,
  },
  {
    name: "Sneha Patil",
    role: "Backend Engineer",
    company: "Cloud Systems",
    profileImage:
      "https://ui-avatars.com/api/?name=Sneha+Patil&background=0D8ABC&color=fff",
    text: "My portfolio helped me stand out. Recruiters actually checked my projects and I got 3 interview calls in the first month. Highly recommend!",
    timestamp: "3w",
    likes: 62,
  },
  {
    name: "Rahul Mehta",
    role: "Full Stack Developer",
    company: "Digital Innovations",
    profileImage:
      "https://ui-avatars.com/api/?name=Rahul+Mehta&background=0D8ABC&color=fff",
    text: "AceTechies portfolio made my profile look professional and credible. It's like having a digital business card that works 24/7.",
    timestamp: "1mo",
    likes: 89,
  },
  {
    name: "Priya Sharma",
    role: "UI/UX Designer",
    company: "Design Studio Pro",
    profileImage:
      "https://ui-avatars.com/api/?name=Priya+Sharma&background=0D8ABC&color=fff",
    text: "The portfolio builder is incredibly easy to use. I was able to showcase my design work beautifully and landed my dream job!",
    timestamp: "1mo",
    likes: 103,
  },
  {
    name: "Vikram Singh",
    role: "DevOps Engineer",
    company: "Infrastructure Labs",
    profileImage:
      "https://ui-avatars.com/api/?name=Vikram+Singh&background=0D8ABC&color=fff",
    text: "I was skeptical at first, but the results speak for themselves. My profile views increased by 300% after sharing my AceTechies portfolio.",
    timestamp: "2mo",
    likes: 78,
  },
  {
    name: "Ananya Desai",
    role: "Software Engineer",
    company: "StartupHub",
    profileImage:
      "https://ui-avatars.com/api/?name=Ananya+Desai&background=0D8ABC&color=fff",
    text: "Best decision I made for my career. The portfolio helped me transition from a service-based company to a product startup!",
    timestamp: "2mo",
    likes: 134,
  },
];

export default function PortfolioPanel() {
  const history = useHistory();
  const { userInfo } = useSelector((state) => state.userLogin);
  const { userData, loading } = useSelector(
    (state) => state.techProfileGetPreviousUserDataStore,
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (userInfo) {
      dispatch(techProfileGetUserPreviousDataAction());
    }
  }, [userInfo, dispatch]);

  // Determine portfolio status and link
  const isPortfolioActive = userData?.data?.isActive === "active";
  const portfolioUsername = userData?.data?.username;

  const generatedPortfolioLink = portfolioUsername
    ? `https://acetechs.in/portfolio/${portfolioUsername}`
    : null;

  // Decide which link to display (use generated link if active)
  const displayLink = generatedPortfolioLink;

  const hasPortfolio = Boolean(displayLink);

  const handleActivatePortfolio = () => {
    history.push(`/techprofile/edit/${userInfo?._id}`);
  };

  return (
    <div className="panel portfolio-panel">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="portfolio-header"
      >
        <h2>Portfolio</h2>
      </motion.div>

      {loading ? (
        <MySkeletonLoader width={"100"} height={100} />
      ) : hasPortfolio ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="portfolio-live-card"
        >
          <div className="portfolio-info">
            <p className="portfolio-label">
              {isPortfolioActive
                ? "Your portfolio is live"
                : "Portfolio inactive"}
            </p>
            <a
              href={displayLink}
              target="_blank"
              rel="noreferrer"
              className="portfolio-link"
            >
              {displayLink}
            </a>
          </div>

          <div className="portfolio-actions">
            {isPortfolioActive ? (
              <button
                onClick={() =>
                  history.push(`/techprofile/edit/${userInfo?._id}`)
                }
                className="workshop-button purple"
              >
                Edit Portfolio
              </button>
            ) : (
              <button
                onClick={handleActivatePortfolio}
                className="workshop-button purple"
              >
                Activate Portfolio
              </button>
            )}
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="portfolio-empty"
        >
          <p className="empty-title">No portfolio created yet</p>
          <p className="empty-subtitle">
            Create a professional portfolio to showcase your skills and
            projects.
          </p>

          <Button
            onClick={() => history.push(`/techprofile/edit/${userInfo?._id}`)}
            className="portfolioPanel_create_button"
          >
            Create Your Portfolio Now
          </Button>
        </motion.div>
      )}

      {/* Social Proof Section */}
      <div className="portfolio-social-proof">
        <h3>What users say after creating their portfolio</h3>

        <div className="testimonials-slider-container">
          <Swiper
            modules={[Autoplay, Pagination]}
            spaceBetween={16}
            slidesPerView={1}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            pagination={{ clickable: true }}
            loop={true}
            breakpoints={{
              640: { slidesPerView: 1.5 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 2.5 },
              1280: { slidesPerView: 3 },
            }}
          >
            {testimonials.map((item, index) => (
              <SwiperSlide key={index}>
                <motion.div
                  whileHover={{ scale: 1.02, y: -4 }}
                  transition={{ duration: 0.2 }}
                  className="linkedin-post-card"
                >
                  {/* Header */}
                  <div className="post-header">
                    <img
                      src={item.profileImage}
                      alt={item.name}
                      className="profile-avatar"
                    />
                    <div className="profile-info">
                      <h4 className="profile-name">{item.name}</h4>
                      <p className="profile-role">{item.role}</p>
                      <p className="profile-meta">
                        {item.company} • {item.timestamp}
                      </p>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="post-content">
                    <p>{item.text}</p>
                  </div>

                  {/* Footer */}
                  <div className="post-footer">
                    <div className="post-engagement">
                      <span className="engagement-icon">👍</span>
                      <span className="engagement-count">{item.likes}</span>
                    </div>
                  </div>

                  {/* LinkedIn-style border top */}
                  <div className="linkedin-accent"></div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
}
